import { onCLS, onINP, onLCP } from 'web-vitals'
import manifest from '../../../config/measurement_events.json'
import { createCollector, completionState, validityCategory } from './measurement_core.mjs'
import { createDestinations, createPixelBridge } from './measurement_destinations.mjs'

const uuid = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID()
  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-')
}
let store
try { store = window.sessionStorage } catch (_) { /* Private mode. */ }
let destinationStore
try { destinationStore = window.localStorage } catch (_) { /* In-memory deduplication is sufficient for this page. */ }
const destinations = createDestinations({
  manifest, storage: destinationStore,
  google: (name, params) => {
    if (typeof window.gtag !== 'function') return false
    window.gtag('event', name, params); return true
  },
  meta: (message, config) => pixel.send(message, config),
  dataLayer: value => { window.dataLayer = window.dataLayer || []; window.dataLayer.push(value) }
})
const pixel = createPixelBridge({ window, document, onReady: () => destinations.flush() })
const collector = createCollector({
  manifest, storage: store, uuid,
  transport: async (endpoint, envelope, options) => {
    const response = await fetch(endpoint, {
      method: 'POST', credentials: 'same-origin', keepalive: options.keepalive,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' },
      body: JSON.stringify(envelope)
    })
    return response.ok && response.status !== 204 ? response.json() : null
  },
  onEvent: (event, config) => destinations.emit(event, config)
})

window.MyMallsMeasurement = {
  emit: (name, properties) => collector.emit(name, properties),
  flush: () => collector.flush(),
  active: () => collector.config.active
}
const emit = window.MyMallsMeasurement.emit
const visible = element => element?.isConnected && element.getClientRects().length > 0 &&
  getComputedStyle(element).visibility !== 'hidden' && !element.closest('[hidden], [inert]')
const json = value => { try { return JSON.parse(value || '{}') } catch (_) { return {} } }
const safeContext = (name, context) => Object.fromEntries(Object.entries(context).filter(([key]) => manifest.events[name]?.parameters.includes(key)))
let currentLocation
let lastScreen
let registrationStarted = false
let lastInteractedField
let initialScreen
let seenViews = new Set()
let seenOnce = new Set()
const fields = new WeakMap()

function configure(config, { navigation = false } = {}) {
  if (config.background_validation) { scan(document); return }
  const previousJourney = collector.config.journey_id
  if (navigator.globalPrivacyControl || document.cookie.split('; ').includes('measurement_opt_out=1')) config = { enabled: false, platform_enabled: false }
  collector.configure(config)
  destinations.configure(collector.config)
  if (previousJourney !== collector.config.journey_id) { seenOnce = new Set(); registrationStarted = false }
  if (!collector.config.active) return
  initialScreen = initialScreen || config.screen_name
  window.mymallsMeasurementOwnsEvents = true
  // Raw URLs are used only for local navigation comparison, never collected.
  const locationKey = location.pathname + location.search
  if (currentLocation !== locationKey || (navigation && lastScreen !== config.screen_name)) {
    currentLocation = locationKey
    lastScreen = config.screen_name
    seenViews = new Set()
    emit('page_view')
    for (const definition of config.view_events || []) {
      const once = definition.once_key && definition.event_name + ':' + definition.once_key
      if (once && seenOnce.has(once)) continue
      if (once) seenOnce.add(once)
      emit(definition.event_name, definition.properties)
    }
  }
  for (const event of config.confirmed_events || []) destinations.emit(event, collector.config)
  destinations.flush()
  scan(document)
}

function view(element, name, properties, key) {
  if (!visible(element) || !collector.config.active || seenViews.has(key)) return
  if (emit(name, safeContext(name, properties))) seenViews.add(key)
}
const observed = new WeakSet()
const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
  for (const entry of entries) if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
    const el = entry.target
    view(el, el.dataset.measurementView, json(el.dataset.measurementProperties), el.dataset.measurementKey || el)
  }
}, { threshold: [0.25] }) : null

const fieldDefinitions = {
  registration_email_form: { email: ['#registration_email'] },
  signup_form: {
    first_name: ['#customer_first_name'], last_name: ['#customer_last_name'],
    date_of_birth: ['#customer_date_of_birth_1i', '#customer_date_of_birth_2i', '#customer_date_of_birth_3i'],
    country_id: ['#customer_country_id'], service_point_id: ['#customer_service_point_id'],
    phone_number: ['[x-ref="localPhone"]', '[name="customer[phone]"]:not([type="hidden"])', '[x-ref="phoneFallback"]']
  }
}
function registrationFields(form) {
  const existing = fields.get(form) || new Map()
  for (const [name, selectors] of Object.entries(fieldDefinitions[form.id] || {})) {
    const components = [...new Set(selectors.flatMap(selector => [...form.querySelectorAll(selector)]))]
      .filter(component => component.type !== 'hidden' && !component.disabled && visible(component))
    if (!components.length) continue
    if (!existing.has(name)) existing.set(name, {
      name, components, state: completionState(components), interacted: false,
      required: name === 'date_of_birth' || components.some(component => component.required)
    })
    else existing.get(name).components = components
  }
  fields.set(form, existing)
  return [...existing.values()].filter(field => field.components.some(visible))
}
function formProperties(form) { return { flow_name: 'register', step_name: form.id === 'signup_form' ? 'profile' : 'email' } }
function startRegistration() {
  if (!registrationStarted) { registrationStarted = true; emit('registration_started', { flow_name: 'register', method: collector.config.auth_method || 'email' }) }
}
function fieldSnapshot(form, field, { initial = false } = {}) {
  const state = completionState(field.components)
  const properties = { ...formProperties(form), field_name: field.name, field_state: state, required: field.required, interacted: field.interacted, ...(field.component ? { field_component: field.component } : {}) }
  if (initial) view(field.components[0], 'form_field_viewed', properties, 'field:' + properties.step_name + ':' + field.name)
  else if (state !== field.state) emit('form_field_state_changed', { ...properties, previous_state: field.state })
  field.state = state
}
const submissions = new WeakMap()
function submitAttempt(form) {
  const previous = submissions.get(form)
  if (previous && performance.now() - previous.at < 150) return previous
  startRegistration()
  const eligible = registrationFields(form)
  eligible.forEach(field => fieldSnapshot(form, field))
  const required = eligible.filter(field => field.required)
  const attempt = { number: (previous?.number || 0) + 1, at: performance.now(), invalid: new Set() }
  submissions.set(form, attempt)
  emit('form_submit_attempted', { ...formProperties(form), attempt_number: attempt.number,
    required_count: required.length, missing_count: required.filter(field => field.state !== 'filled').length,
    invalid_count: eligible.filter(field => validityCategory(field.components)).length })
  return attempt
}
function trackedField(target) {
  const form = target.closest?.('#signup_form, #registration_email_form')
  if (!form) return {}
  return { form, field: registrationFields(form).find(field => field.components.includes(target)) }
}
function scan(root) {
  if (!collector.config.active) return
  for (const el of root.querySelectorAll('[data-measurement-view]')) {
    if (!observed.has(el)) { observed.add(el); intersection?.observe(el) }
    if (!intersection) view(el, el.dataset.measurementView, json(el.dataset.measurementProperties), el.dataset.measurementKey || el)
  }
  for (const form of root.querySelectorAll('#signup_form, #registration_email_form')) {
    registrationFields(form).forEach(field => fieldSnapshot(form, field, { initial: true }))
  }
  // Alpine can reveal existing panels without a navigation or a new compiler run.
  for (const el of root.querySelectorAll('[data-measurement-view]')) {
    if (visible(el) && el.dataset.measurementVisible === 'true') view(el, el.dataset.measurementView, json(el.dataset.measurementProperties), el.dataset.measurementKey || el)
  }
}

document.addEventListener('pointerdown', event => {
  const { form, field } = trackedField(event.target)
  if (field) {
    if (field.name === 'date_of_birth') field.component = ['year', 'month', 'day'][field.components.indexOf(event.target)]
    startRegistration()
    if (lastInteractedField !== field) emit(field.interacted ? 'form_field_revisited' : 'form_field_interacted', {
      ...formProperties(form), field_name: field.name, field_state: field.state, required: field.required, interacted: true,
      ...(field.component ? { field_component: field.component } : {})
    })
    field.interacted = true; lastInteractedField = field
  }
}, true)
document.addEventListener('keydown', event => {
  const { form, field } = trackedField(event.target)
  if (field && !['Tab', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
    if (field.name === 'date_of_birth') field.component = ['year', 'month', 'day'][field.components.indexOf(event.target)]
    startRegistration()
    if (lastInteractedField !== field) emit(field.interacted ? 'form_field_revisited' : 'form_field_interacted', {
      ...formProperties(form), field_name: field.name, field_state: field.state, required: field.required, interacted: true,
      ...(field.component ? { field_component: field.component } : {})
    })
    field.interacted = true
    lastInteractedField = field
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') submitAttempt(form)
  }
}, true)
for (const type of ['change', 'focusout']) document.addEventListener(type, event => {
  const { form, field } = trackedField(event.target)
  if (field) fieldSnapshot(form, field)
}, true)
document.addEventListener('invalid', event => {
  const { form, field } = trackedField(event.target)
  if (!field) return
  const attempt = submitAttempt(form)
  if (attempt.invalid.has(field.name)) return
  attempt.invalid.add(field.name)
  emit('form_error', { ...formProperties(form), field_name: field.name, validation_source: 'browser',
    error_category: validityCategory(field.components) || 'invalid', attempt_number: attempt.number })
}, true)
document.addEventListener('submit', event => {
  const form = event.target
  if (fieldDefinitions[form.id]) submitAttempt(form)
  if (form.matches('[data-measurement-submit]')) emit(form.dataset.measurementSubmit, json(form.dataset.measurementProperties))
  collector.flush({ keepalive: true })
}, true)
document.addEventListener('click', event => {
  const button = event.target.closest('button, input[type="submit"]')
  if (button?.form && fieldDefinitions[button.form.id] && (button.type === 'submit' || !button.type)) submitAttempt(button.form)
  const el = event.target.closest('[data-measurement-click]')
  if (el && visible(el)) emit(el.dataset.measurementClick, json(el.dataset.measurementProperties))
  const form = event.target.closest('#signup_form, #registration_email_form')
  const link = event.target.closest('a[href]')
  if (link && !el) {
    const href = link.getAttribute('href') || ''
    if (href.startsWith('javascript:Intercom')) emit('support_opened', { placement: 'suite' })
    else {
      let url
      try { url = new URL(href, location.origin) } catch (_) { /* Malformed navigation. */ }
      if (url) {
        const path = url.pathname
        if (url.hostname === 'support.mymalls.com') emit('help_content_viewed', { section_id: 'support_article' })
        else if (url.origin === location.origin) {
          const action = /\/checkout/.test(path) ? 'pay' : /\/supplier_invoices|\/tracking_number_registrations/.test(path) ? 'upload_invoice' :
            /\/combine|\/consolidat/.test(path) ? 'combine' : /\/register/.test(path) ? 'register' : /\/quickguide/.test(path) ? 'help' : /\/invit/.test(path) ? 'referral' : null
          if (action) emit('cta_clicked', { action, placement: 'suite' })
          if (action && link.closest('[data-measurement-view="action_prompt_viewed"]')) emit('action_prompt_clicked', { action, placement: 'dashboard' })
          const warehouse = url.searchParams.get('warehouse')
          if (warehouse && /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(warehouse) && warehouse !== collector.config.context?.warehouse) emit('warehouse_selected', { warehouse })
          if (/\/packages\/?$/.test(path) && ['state', 'scope', 'filter'].some(key => url.searchParams.has(key))) emit('package_filter_applied', { section_id: 'package_list' })
          if (/\/payments\/[^/]+/.test(path)) emit('receipt_downloaded', { context: 'account' })
        } else if (/dhl|fedex|ups/.test(url.hostname)) {
          emit('carrier_tracking_clicked', { carrier: /dhl/.test(url.hostname) ? 'dhl' : /fedex/.test(url.hostname) ? 'fedex' : 'ups' })
        } else if (/facebook\.com|wa\.me|whatsapp\.com|twitter\.com|x\.com/.test(url.hostname)) {
          emit('share', { method: 'link', placement: 'referral' })
        }
      }
    }
  }
  if (form && link) emit('form_exit_requested', { ...formProperties(form), action: 'back', ...(lastInteractedField ? { field_name: lastInteractedField.name } : {}) })
  if (link && (el || form)) collector.flush({ keepalive: true })
}, true)

const calculatorStarted = new WeakSet()
document.addEventListener('input', event => {
  const form = event.target.closest('form')
  if (form?.querySelector('[name^="quote["]') && !calculatorStarted.has(form)) {
    calculatorStarted.add(form); emit('calculator_started', { flow_name: 'calculator' })
  }
}, true)
document.addEventListener('change', event => {
  const target = event.target
  const form = target.closest('form')
  if (!form) return
  if (collector.config.screen_name?.startsWith('consolidation_requests_') && target.type === 'checkbox') {
    emit('consolidation_selection_changed', { selected_count: form.querySelectorAll('input[type="checkbox"]:checked').length })
  }
  if (collector.config.screen_name?.startsWith('checkout_order_summary_') && target.type === 'checkbox') {
    // This is observed selection intent; committed selection is recorded by Rails.
    emit('checkout_selection_changed', { selected_count: form.querySelectorAll('input[type="checkbox"]:checked').length })
  }
}, true)
document.addEventListener('submit', event => {
  if (event.target.querySelector('[name^="quote["]')) emit('calculator_submitted', { flow_name: 'calculator' })
  if (event.target.querySelector('[name="search"], [name="q"]') && collector.config.screen_name?.startsWith('packages_')) emit('package_search_used')
}, true)
document.addEventListener('toggle', event => {
  if (event.target.tagName === 'DETAILS' && event.target.open) emit('faq_opened', { section_id: event.target.dataset.measurementSection || 'suite_faq' })
}, true)

// The hungry HTML context is compiled after insertion, including overlay
// updates. Validation context updates field states without advancing a view.
up.on('up:fragment:inserted', () => {
  requestAnimationFrame(() => {
    if (collector.config.active) scan(document)
  })
})
up.compiler('#measurement-context', element => {
  const config = json(element.dataset.measurementConfig)
  requestAnimationFrame(() => configure(config, { navigation: true }))
})
document.addEventListener('DOMContentLoaded', () => {
  const el = document.querySelector('#measurement-context')
  if (el) configure(json(el.dataset.measurementConfig))
})
let scanScheduled = false
new MutationObserver(() => {
  if (!collector.config.active || scanScheduled) return
  scanScheduled = true
  requestAnimationFrame(() => { scanScheduled = false; scan(document) })
}).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'hidden', 'class'] })
window.addEventListener('pagehide', () => collector.flush({ keepalive: true }))
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') collector.flush({ keepalive: true }) })
setInterval(() => { destinations.flush(); collector.flush() }, 2500)

// Document-level vitals use the official implementation (including CLS session
// windows and INP's interaction percentile). Unpoly route timing is separate.
for (const observe of [onCLS, onINP, onLCP]) {
  observe(metric => {
    if (!Number.isFinite(metric.value) || metric.value < 0) return
    emit('performance_measured', { metric_name: metric.name.toLowerCase(), metric_value: metric.value, metric_id: metric.id,
      ...(initialScreen ? { screen_name: initialScreen } : {}) })
    collector.flush({ keepalive: true })
  })
}
